import { AnyAction, Middleware } from 'redux'
import { getType, isActionOf } from 'typesafe-actions'
import initRouterParamsSelector, { Params } from '../../selectors/initRouterParamsSelector'
import initParametersSelector from '../../selectors/initParametersSelector'
import { setInitParametersToState } from '../../actions/app/initParameters.actions'
import {
    fetchEntryData,
    fetchEntryDataAsync,
    resetEntryDataState,
} from '../../actions/app/entryData.actions'
import currentPathnameSelector from '../../selectors/router/currentPathnameSelector'
import { ROUTE_CHECK } from '../../../constants/routes'
import { resetCartState } from '../../actions/app/cart.actions'
import { resetCarlineSelectionState } from '../../actions/app/carlineSelection.actions'
import { resetModelDataState } from '../../actions/app/models.actions'
import { resetConfigurationDataState } from '../../actions/app/configuration.actions'
import { resetEquipmentGroupsState } from '../../actions/app/equipmentGroups.actions'
import { resetFilters } from '../../actions/app/filter.actions'
import { init } from '../../actions/core/init.actions'
import { createNotification, resetNotificationsState } from '../../actions/app/notification.actions'
import { translateForDisplay } from '@avp/core'
import { vehicleCodeChanged } from '../../actions/app/vehicleCode.actions'
import failedRequestsSelector from '../../selectors/failedRequestsSelector'
import { applyBrand } from '../../actions/app/brand.actions'
import { resetVwnModelsWithRecommendationsData, resetVwnRecommendationsForModelData } from '../../actions/app/vwnRecommendation.actions'
import { resetAiRecommendationsForModelData } from '../../actions/app/aiRecommendation.actions'
import isClearOnInitEnabledSelector from '../../selectors/feature-scope/isClearOnInitEnabledSelector'
import { Dispatch } from 'react'
import { resetVisualizationState } from '../../actions/app/visualization.actions'
import { resetActiveRenderImgIdx } from '../../actions/app/renderImages.actions'
import { resetPrStringStatusState } from '../../actions/app/prString.actions'
import { resetComparisonData } from '../../actions/app/comparisonData.actions'
import { resetComparisonResultData } from '../../actions/app/comparisonResultData.actions'
import { sessionIdManager } from '../../../utils/sessionIdManager'
import { fetchCarlineData } from '../../actions/app/carlineData.actions'
import sessionIdSelector from '../../selectors/sessionIdSelector'
import carlineDataSelector from '../../selectors/carlines/carlineDataSelector'

const mapRouterParamsToInitParams = (routerParams: Params): Record<string, string> => ({
    contextId: routerParams.context || routerParams.mandant,
    dealerId: routerParams.dealer_number,
    salesmanId: routerParams.salesman_number,
    carstoreUserId: routerParams.carstore_user_id,
    returnUrl: routerParams.return_url,
    features: routerParams.features,
    vehicleCode: routerParams.vehicle_code,
    token: routerParams.token,
    entryUrl: window.location.href,
    debugTranslationText: routerParams.d_text,
    dccLoggerEnabled: routerParams.dccLoggerEnabled,
})

const handleDccLoggerParameter = (initParameters: Record<string, string>) => {
    if (initParameters.dccLoggerEnabled === '1') {
        sessionStorage.setItem('dccLoggerEnabled', '1')
    } else if (initParameters.dccLoggerEnabled === '0') {
        sessionStorage.removeItem('dccLoggerEnabled')
    }
}

const resetApplicationDataStates = (dispatch: Dispatch<AnyAction>, initParameters: Record<string, string>) => {
    sessionIdManager.clear()
    dispatch(resetEntryDataState())
    dispatch(resetCartState())
    dispatch(resetCarlineSelectionState())
    dispatch(resetModelDataState())
    dispatch(resetConfigurationDataState())
    dispatch(resetEquipmentGroupsState())
    dispatch(resetFilters())
    dispatch(resetVisualizationState())
    dispatch(resetActiveRenderImgIdx())
    dispatch(resetVwnModelsWithRecommendationsData())
    dispatch(resetVwnRecommendationsForModelData())
    dispatch(resetAiRecommendationsForModelData())
    dispatch(setInitParametersToState(initParameters))
    dispatch(resetPrStringStatusState())
    dispatch(resetComparisonData())
    dispatch(resetComparisonResultData())
}

const fetchMissingContextData = (dispatch: Dispatch<AnyAction>, state): void => {
    if (!sessionIdSelector(state)) {
        dispatch(fetchEntryData())
    } else if (
        !carlineDataSelector(state)?.carlineGroups?.length
        && !failedRequestsSelector(state).some(({ type }) => type === getType(fetchCarlineData))
    ) {
        dispatch(fetchCarlineData())
    }
}

const initMiddleware: Middleware = (store) => (next) => (action) => {
    next(action)

    if (isActionOf(init, action)) {
        const { getState, dispatch } = store
        const failedRequests = failedRequestsSelector(getState())

        failedRequests
            .filter(failedRequest => failedRequest.type !== getType(fetchEntryDataAsync.request) && failedRequest.type !== getType(fetchEntryData))
            .forEach((failedRequest) => {
                dispatch(failedRequest)
            })

        dispatch(applyBrand())
        dispatch(resetNotificationsState())

        const initRouterParams = initRouterParamsSelector(getState())
        const initStateParams = initParametersSelector(getState())
        const currentPathname = currentPathnameSelector(getState())
        const routerContextParamValue = initRouterParams.context || initRouterParams.mandant
        const isClearOnInitEnabled = isClearOnInitEnabledSelector(getState())

        if (currentPathname === ROUTE_CHECK) {
            return
        }

        if (!routerContextParamValue && !initStateParams.contextId) {
            // eslint-disable-next-line no-alert
            alert('No context or mandant parameter provided')
            return
        }

        if (routerContextParamValue && !initStateParams.contextId) {
            const initParameters = mapRouterParamsToInitParams(initRouterParams)

            handleDccLoggerParameter(initParameters)

            dispatch(setInitParametersToState(initParameters))
            dispatch(fetchEntryData())

            return
        }

        if (initStateParams.vehicleCode !== initRouterParams.vehicle_code) {
            const initParameters = mapRouterParamsToInitParams(initRouterParams)

            handleDccLoggerParameter(initParameters)

            dispatch(setInitParametersToState(initParameters))
            dispatch(vehicleCodeChanged())
        }

        if (initStateParams.debugTranslationText !== initRouterParams.d_text) {
            const initParameters = mapRouterParamsToInitParams(initRouterParams)

            handleDccLoggerParameter(initParameters)

            dispatch(setInitParametersToState(initParameters))
        }

        if (!routerContextParamValue && initStateParams.contextId) {
            fetchMissingContextData(dispatch, getState())
            return
        }

        if (
            initStateParams.contextId !== routerContextParamValue ||
            initStateParams.token !== initRouterParams.token
        ) {
            const initParameters = mapRouterParamsToInitParams(initRouterParams)

            handleDccLoggerParameter(initParameters)
            resetApplicationDataStates(dispatch, initParameters)

            dispatch(fetchEntryData())

            dispatch(
                createNotification({
                    type: 'info',
                    message: translateForDisplay('notifications.context-changed'),
                }),
            )
        } else if (isClearOnInitEnabled) {
            const initParameters = mapRouterParamsToInitParams(initRouterParams)
            handleDccLoggerParameter(initParameters)
            resetApplicationDataStates(dispatch, initParameters)
            dispatch(fetchEntryData())
        } else {
            fetchMissingContextData(dispatch, getState())
        }
    }
}

export default initMiddleware
